import styles from "./Faq.module.scss";

import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import { Container, Row, Col, Collapse } from "react-bootstrap";

import { AppContext } from "index.js";

function Qa({ id, q, a }) {
  const [open, setOpen] = useState(false);
  const onQaClick = function () {
    if (open) return;
    window.dataLayer.push({
      event: "faq-open",
      faq: id,
    });
    setOpen(true);
  };

  return (
    <div
      className={classNames(styles.qa, "mx-n3 mx-md-0 mb-3 svg-shadow")}
      onClick={onQaClick}
      aria-controls={id}
      aria-expanded={open}
    >
      <p className={styles.q}>{q}</p>
      <Collapse in={open}>
        <div id={id} className={styles.a}>
          {a}
        </div>
      </Collapse>
    </div>
  );
}

export default function Faq() {
  const appContext = useContext(AppContext);

  return (
    <div className={styles.component}>
      <Helmet>
        <title>
          {appContext.intl.formatMessage({
            id: "Faq.pageTitle",
            defaultMessage: "Flora FAQ",
          })}
        </title>
      </Helmet>
      <Container>
        <Row className="justify-content-center">
          <Col xs="12" md="11" lg="10" xl="9">
            <header>
              <h1 className="mt-5 mb-3">
                <FormattedMessage
                  id="Faq.title"
                  defaultMessage="<primary>Flora</primary> FAQ"
                  values={{
                    primary: (msg) => (
                      <span className="text-primary-dark">{msg}</span>
                    ),
                  }}
                />
              </h1>
              <p>
                <FormattedMessage
                  id="Faq.text"
                  defaultMessage="Here, you can find the answers to some frequently asked questions. If you did not find the answer to your question, please feel free to contact us via the in-app feedback system (which you can find on the <ui>More</ui> screen in the app) or via email at <a>support@flora.appfinca.com</a>."
                  values={{
                    a: (msg) => (
                      <a href="mailto:support@flora.appfinca.com">{msg}</a>
                    ),
                    ui: (msg) => `“${msg}”`,
                  }}
                />
              </p>
            </header>
            <section id="sec-focus-trees">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secFocusTrees.title"
                  defaultMessage="Focus Trees"
                />
              </h3>
              <Qa
                id="select-seed"
                q={
                  <FormattedMessage
                    id="Faq.selectSeed.q"
                    defaultMessage="Q: I keep growing the same plants. How to grow different types of trees?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.selectSeed.a"
                    defaultMessage="<p>Please tap on the <ui>Seed</ui> icon on the main screen before pressing the <ui>Start</ui> button. Then, you enter the <ui>Grand Tour</ui> screen and can choose different types of plants to unlock at different stops. Have a nice trip<em>!</em></p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="offline-planting"
                q={
                  <FormattedMessage
                    id="Faq.offlinePlanting.q"
                    defaultMessage="Q: Can I plant a tree while staying offline?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.offlinePlanting.a"
                    defaultMessage="<p>Yes. Flora supports the <ui>Offline Planting</ui> mode. To plant a tree offline, please disconnect from the Internet <em>before</em> pressing the <ui>Start</ui> button. Note, however, that</p><ul><li>your friends will no longer be able to discover and join your room; and</li><li>you will not be able to report a False Detection if the tree is killed</li></ul><p>because there is no Internet connection.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      ul: (msg) => <ul>{msg}</ul>,
                      li: (msg) => (
                        <li>
                          <p>{msg}</p>
                        </li>
                      ),
                    }}
                  />
                }
              />
              <Qa
                id="notified-after-planting"
                q={
                  <FormattedMessage
                    id="Faq.notifiedAfterPlanting.q"
                    defaultMessage="Q: How to get notified when a tree grows up?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.notifiedAfterPlanting.a"
                    defaultMessage="<p>On the <ui>More</ui> screen, please make sure that  you have allowed Flora to send notifications. Then, even if your phone is locked, you will still receive a notification when your tree successfully grows up.</p><p>Note that you won't receive notifications while your phone screen is on because you can see the result directly.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="use-other-apps-while-planting"
                q={
                  <FormattedMessage
                    id="Faq.useOtherAppsWhilePlanting.q"
                    defaultMessage="Q: Can I use other tooling apps (e.g. a dictionary) during a focus session without killing a tree?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.useOtherAppsWhilePlanting.a"
                    defaultMessage="<p>Yes. There are two ways to use other apps while growing a tree:</p><ol><li>If you only need to use other apps occasionally, simply press the <ui>Pause</ui> button when growing a tree. Then you can go to any other apps without killing the tree. When done, go back to Flora and press the <ui>Resume</ui> button to continue the session. The pause time will be subtracted from the final duration of the focus session. Note that the Pause feature is available only when there is no price associated with the tree. To see how the Price feature works, please click on the <ui>?</ui> (question mark) icon next to the <ui>Price</ui> menu on the main screen.</li><li>If you constantly need certain apps for work, go to the <ui>More</ui> screen and switch the <ui>Allow apps during Focus</ui> option on. Then, you can select white-listed apps. During a focus session, you can leave the Flora app without killing the tree. But you cannot open the apps not in the whitelist. This feature is available on devices with iOS 16.1+.</li></ol>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      ol: (msg) => <ol>{msg}</ol>,
                      li: (msg) => (
                        <li>
                          <p>{msg}</p>
                        </li>
                      ),
                    }}
                  />
                }
              />
              <Qa
                id="finish-earlier-while-planting"
                q={
                  <FormattedMessage
                    id="Faq.finishEarlierWhilePlanting.q"
                    defaultMessage="Q: What if I finish my task before a tree grows up?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.finishEarlierWhilePlanting.a"
                    defaultMessage="<p>Just press the <ui>Pause</ui> button, and then you can leave Flora to do whatever you want. Your total focus duration will be subtracted the pause time and remain correct.</p><p>Note that the Pause feature is available only when there is no price associated with the tree. To see how the Price feature works, please click on the <ui>?</ui> (question mark) icon next to the <ui>Price</ui> menu on the main screen.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="finish-late-while-planting"
                q={
                  <FormattedMessage
                    id="Faq.finishLateWhilePlanting.q"
                    defaultMessage="Q: What if I need more time to finish my task after a tree grows up?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.finishLateWhilePlanting.a"
                    defaultMessage="<p>A focus session does <em>not</em> end when the tree grows. So, keep working! During this phase, the focus duration will automatically count up to record the extra time you input. When done, you can press the <ui>See Tree</ui> button to end the session and see your tree. Your total focus duration, including your extra time after the tree grows, will be correctly recorded in the associated story.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="disable-pause"
                q={
                  <FormattedMessage
                    id="Faq.disablePause.q"
                    defaultMessage="Q: Can I turn off the Pause feature?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.disablePause.a"
                    defaultMessage="<p>Yes. To turn off this feature, switch off the <ui>Allow pauses during Focus</ui> option on the <ui>More</ui> screen.</p><p>You can also set a non-zero Price before growing a tree, which automatically disables the Pause feature for the next focus session. To see how the Price feature works, please click on the <ui>?</ui> (question mark) icon next to the <ui>Price</ui> menu on the main screen.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="back-to-work"
                q={
                  <FormattedMessage
                    id="Faq.backToWork.q"
                    defaultMessage="Q: Can I adjust the break timer after growing a tree?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.backToWork.a"
                    defaultMessage="<p>Yes. After successfully growing a tree, press the <ui>Timer</ui> icon at the left of the <ui>See Stories</ui> button on the green focus screen, then you will be able to adjust the break timer. When the timer is up, a <ui>Back to Work</ui> notification will be sent to remind you to go back to work.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="false-kill"
                q={
                  <FormattedMessage
                    id="Faq.falseKill.q"
                    defaultMessage="Q: I didn't leave Flora but my tree was still killed. What should I do?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.falseKill.a"
                    defaultMessage="<p>If you get your tree killed and think it was not your fault, please report a <ui>False Detection</ui> by pressing the <ui>!</ui> (triangular 
                    exclamation) button at the upper-right corner of the red focus screen. If verifying that it is indeed a false detection, we will revive your tree. If we find any bug that leads to a false kill, we will fix it ASAP. Meanwhile, you can take the following steps to minimize the chance of false detection:</p><ul><li>Do <em>not</em> press the home button before locking your phone during a focus session. Some people have this habit, but it kills the tree. Please leave the Flora app open.</li><li>Keep the version of your iOS and Flora app up to date.</li><li>Make sure you have a stable Internet connection. If you foresee connectivity issues, please grow a tree in the Offline Planting mode.</li><li>The iOS system may actively kill apps under some circumstances, such as when it faces memory pressure or when the phone is locked for a long time. If the Flora app is unfortunately killed by the iOS, your session will end. Flora will do its best to restore your focus session when it is opened again. However, please understand that we might not be able to always successfully recover your focus session due to iOS limitations. You can minimize the chance of an automatic shutdown by closing some memory-hungry apps (like games) in the background.</li></ul>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      ul: (msg) => <ul>{msg}</ul>,
                      li: (msg) => (
                        <li>
                          <p>{msg}</p>
                        </li>
                      ),
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
            </section>
            <section id="sec-group-planting">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secGroupPlanting.title"
                  defaultMessage="Group Focus"
                />
              </h3>
              <Qa
                id="make-friends"
                q={
                  <FormattedMessage
                    id="Faq.makeFriends.q"
                    defaultMessage="Q: How to make friends in Flora?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.makeFriends.a"
                    defaultMessage="<p>Share your QR code displayed on the <ui>Friends</ui> screen to your friends, or scan the QR codes shared by your friends. You can also quickly make friends with other Flora users in your contact list by pressing the <ui>Import from Contacts</ui> button on the <ui>Friends</ui> screen, respectively.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="joint-planting"
                q={
                  <FormattedMessage
                    id="Faq.jointPlanting.q"
                    defaultMessage="Q: How to grow a tree with friends?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.jointPlanting.a"
                    defaultMessage="<p>There are two ways: you can either host or join a room.</p><p>To host a room, tap on the <ui>People</ui> icon at the left of the <ui>Start</ui> button on the main screen, and then select the <ui>Friends</ui> or <ui>Selected Partners</ui> options. You can also share the passcode of your room to other Flora users. To see the passcode, press the <ui>Key</ui> icon at the upper-right corner of the main screen. Other people can discover and join your room only after you press the <ui>Start</ui> button on the main screen.</p><p>To join someone else’s room, first you either find your friends’ rooms on the <ui>Friends</ui> screen or enter your friend’s passcode after pressing the <ui>Key</ui> icon on the main screen. Then, you can preview their rooms (for example, seeing the time left) to decide whether to join a room or not. As a guest, you can select your own plant to grow by tapping on the <ui>Seed</ui> icon on the preview screen. You then start growing a tree with the host by pressing the <ui>Join</ui> button on the preview screen.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="kill-tree-while-joint-planting"
                q={
                  <FormattedMessage
                    id="Faq.killTreeWhileJointPlanting.q"
                    defaultMessage="Q: What will happen if I leave Flora during a group focus session?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.killTreeWhileJointPlanting.a"
                    defaultMessage="<p>If you are the host of the session, <em>everyone’s tree</em> will be killed because the session will be terminated. If you are a guest, on the other hand, you will only kill the tree grown by yourself. Other people will continue growing their trees in the session.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="joint-planting-benefits"
                q={
                  <FormattedMessage
                    id="Faq.jointPlantingBenefits.q"
                    defaultMessage="Q: When and why should I grow trees with other people?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.jointPlantingBenefits.a"
                    defaultMessage="<p>You can always invite your family, friends, and colleagues to plant a tree together while you work, enjoy meals together, or try to go to sleep on time, etc. Psychologists and researchers, such as Festinger (1957), Heider (1946), and Newcomb (1953), have found that this will boost your productivity <em>in the long term</em> because your commitment to them keeps your behavior in sync. Group focus sessions can also strengthen your relationships as the sense of accomplishing something together brings you closer.</p><p>Flora encourages joint tree planting by allowing each participant of a successful group focus session to win an <em>additional</em> tree grown by a random peer. Are you ready to win your friend’s trees?</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
            </section>
            <section id="sec-garden-stories">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secHabitTracker.title"
                  defaultMessage="Garden and Stories"
                />
              </h3>
              <Qa
                id="total-focused-time"
                q={
                  <FormattedMessage
                    id="Faq.totalFocusedTime.q"
                    defaultMessage="Q: Where can I find my total focused time?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.totalFocusedTime.a"
                    defaultMessage="<p>Tap on your avatar in the garden to go to the <ui>Badges</ui> screen. You will see the total amount of time that you have logged and how far you are away from the next badge.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="garden-look"
                q={
                  <FormattedMessage
                    id="Faq.gardenLook.q"
                    defaultMessage="Q: How to improve the look of my Garden?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.gardenLook.a"
                    defaultMessage="<p>In your Garden, plants are arranged in a row based on their planting date, with the most recent ones on the right. This setup gives you complete control over your Garden’s appearance. To enhance its beauty, consider these tips when deciding what to plant next:</p><ul><li>Avoid planting trees in succession. Since trees are spaced further apart, this can leave empty spaces in your Garden. Instead, intersperse them with flowers or fruit plants to create a denser, more vibrant look;</li><li>Try to mix plants with similar or complementary color tones for a harmonious aesthetic;</li><li>Designate certain plants to commemorate special events in your life. This personal touch will make your Garden even more meaningful and unique.</li></ul><p>But don’t stop there! Let your imagination run wild and bring your ideas to life. Happy gardening, and don’t hesitate to share your creativity with your friends!</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      ul: (msg) => <ul>{msg}</ul>,
                      li: (msg) => <li>{msg}</li>,
                    }}
                  />
                }
              />
              <Qa
                id="garden-gap"
                q={
                  <FormattedMessage
                    id="Faq.gardenGap.q"
                    defaultMessage="Q: Why is there a gap (empty space) to the right of the plants in my Garden?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.gardenGap.a"
                    defaultMessage="<p>The trees in your Garden naturally shift towards the left over time. To keep your garden vibrant and full of life, it’s important to plant new trees (and stay focused on tasks) regularly, just as you would care for a real-world garden. Feel free to swipe the Garden view left or right to explore your past trees and check out the real ones you’re currently nurturing.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="big-garden"
                q={
                  <FormattedMessage
                    id="Faq.bigGarden.q"
                    defaultMessage="Q: Can I see all the plants I’ve grown at once?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.bigGarden.a"
                    defaultMessage="<p>Yes. Simply swipe to move to the left of your Garden view to unveil your <ui>Big Garden</ui>. This is where you can admire all your trees in one place. Do keep in mind, the Big Garden is an exclusive feature for <ui>Expert Planters</ui>. Not there yet? No worries! Keep up the fantastic effort and aim for that badge. Before you know it, you’ll be basking in the sight of all your trees together.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="stories"
                q={
                  <FormattedMessage
                    id="Faq.noteStory.q"
                    defaultMessage="Q: Can I add notes to a tree or story?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.noteStory.a"
                    defaultMessage="<p>Yes. You can either note what you will be doing in the <ui>What to focus on next?</ui> area on the main screen before pressing the <ui>Start</ui> button, or note what you have done in the <ui>Attach a message...</ui> area of the associated story on the <ui>My Stories</ui> screen after growing the tree.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="hide-story"
                q={
                  <FormattedMessage
                    id="Faq.hideStory.q"
                    defaultMessage="Q: How to hide a story from my friends?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.hideStory.a"
                    defaultMessage="<p>Tap on the <ui>⋮</ui> (Kebab) button at the upper-right corner of the story, then you can change its visibility. Select the <ui>Only Me</ui> option to be the only person who can see it.</p><p>Note, however, that your story may be a great motivator to your friends and could trigger fun interactions. You will lose these opportunities if you make your story private.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
            </section>
            <section id="sec-todo-list">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secTodoList.title"
                  defaultMessage="To-Do List, Goals, and Habits"
                />
              </h3>
              <Qa
                id="create-todo"
                q={
                  <FormattedMessage
                    id="Faq.createTodo.q"
                    defaultMessage="Q: How to create a to-do item and be reminded of it?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.createTodo.a"
                    defaultMessage="<p>You can create a to-do item by navigating to the <ui>To-Dos</ui> tab on your <ui>Garden</ui> screen and then pressing an <ui>+</ui> (add) button. If you would like to be reminded of the do-to item at a specific time in a week, turn on the <ui>Reminder</ui> in the <ui>To-Do</ui> menu.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="goal-tracking"
                q={
                  <FormattedMessage
                    id="Faq.goalTracking.q"
                    defaultMessage="Q: Can I set up a goal of spending time on a to-do item and track it?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.goalTracking.a"
                    defaultMessage="<p>Yes. First, turn on the <ui>Goal</ui> in the menu of a to-do item. A progress bar will appear in the item. Then, during a focus session, you can tap on the <ui>Tag</ui> icon to tag your tree with the to-do. After the tree grows up successfully, the time you have spent on the session will be automatically added to the progress bar. You can also make progress by tagging your stories shown in the <ui>My Stories</ui> tab with different to-do items.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="reset-goal"
                q={
                  <FormattedMessage
                    id="Faq.resetGoal.q"
                    defaultMessage="Q: Can I set up a repeating goal?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.resetGoal.a"
                    defaultMessage="<p>No problem. In a to-do menu, you can set up a <ui>Repeating Goal</ui> that automatically resets its progress at the beginning of every day, week, or month.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="habit-tracker"
                q={
                  <FormattedMessage
                    id="Faq.habitTracker.q"
                    defaultMessage="Q: How to set up a habit tracker?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.habitTracker.a"
                    defaultMessage="<p>You can track your activities for a specific to-do item by enabling the <ui>Habit Tracker</ui> in the menu of the to-do. Then, you will get a winning streak if you successfully grow trees and tag them with the to-do in successive days.</p><p>Flora also compiles your focus activities into detailed daily, weekly, monthly, and yearly statistics so you can find opportunities to improve your time-management and planning skills. Your stats are shown in the <ui>Stats</ui> tab of the <ui>Garden</ui> screen.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
            </section>
            <section id="sec-widget">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secWidget.title"
                  defaultMessage="Widgets (iOS 14+ Only)"
                />
              </h3>
              <Qa
                id="widget-empty"
                q={
                  <FormattedMessage
                    id="Faq.widgetEmpty.q"
                    defaultMessage="Q: My widget shows nothing. What should I do?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.widgetEmpty.a"
                    defaultMessage="<p>The widget syncs with the main app about every 30 minutes. So, please give it some time. If you have waited for a long time and there’s still nothing in the widget, please</p><ol><li>Update the Flora app to the latest version;</li><li>Open the app and navigate to your <ui>Garden</ui> (this is necessary);</li><li>Remove the widget from your <ui>Home</ui> screen and add it again.</li></ol><p>The content should appear within 30 minutes. If the problem persists, please contact us via the in-app feedback system. We will help you out there.</p>"
                    values={{
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      p: (msg) => <p>{msg}</p>,
                      ol: (msg) => <ol>{msg}</ol>,
                      li: (msg) => (
                        <li>
                          <p>{msg}</p>
                        </li>
                      ),
                    }}
                  />
                }
              />
            </section>
            <section id="sec-payment">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secPayment.title"
                  defaultMessage="Payments"
                />
              </h3>
              <Qa
                id="restore-iap"
                q={
                  <FormattedMessage
                    id="Faq.restoreIap.q"
                    defaultMessage="Q: The tours (e.g., Amazon Rainforest) I bought is missing. What should I do?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.restoreIap.a"
                    defaultMessage="<p>Please go to the <ui>More</ui> screen and press the <ui>Restore In-App Purchases</ui> button. Then, all the torus you bought will be restored.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="price-charge"
                q={
                  <FormattedMessage
                    id="Faq.priceCharge.q"
                    defaultMessage="Q: I set a Price when growing a tree, but later the tree was false killed by the app. Can I get a refund?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.priceCharge.a"
                    defaultMessage="<p>When using the Price feature, you are <em>not</em> charged unless you confirm that you killed a tree by pressing the <ui>Honor My Price</ui> button on the red focus screen. If you get your tree false killed, please report a <ui>False Detection</ui> by pressing the <ui>!</ui> (triangular  exclamation) button at the upper-right corner. If you have pressed the <ui>Honor My Price</ui> button and want a refund, please contact us via email at <a>support@flora.appfinca.com</a> and describe the situation you encountered. We will help you from there.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      a: (msg) => (
                        <a
                          className="text-primary-dark"
                          id="error-code"
                          href="mailto:support@flora.appfinca.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {msg}
                        </a>
                      ),
                    }}
                  />
                }
              />
              <Qa
                id="error-code"
                q={
                  <FormattedMessage
                    id="Faq.errorCode.q"
                    defaultMessage="Q: I got an error code during the payment process. What should I do?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.errorCode.a"
                    defaultMessage="<p>Please email the error code to <a>support@flora.appfinca.com</a> and describe the situation briefly. We will help you from there.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      a: (msg) => (
                        <a
                          className="text-primary-dark"
                          id="error-code"
                          href="mailto:support@flora.appfinca.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {msg}
                        </a>
                      ),
                    }}
                  />
                }
              />
              <Qa
                id="receipt"
                q={
                  <FormattedMessage
                    id="Faq.receipt.q"
                    defaultMessage="Q: Can I have a receipt for my payment?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.receipt.a"
                    defaultMessage="<p>Of course. Please send an email to <a>support@flora.appfinca.com</a> with your account name, account email address, and purchase date and time. We will email you back your receipt.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      a: (msg) => (
                        <a
                          className="text-primary-dark"
                          id="error-code"
                          href="mailto:support@flora.appfinca.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {msg}
                        </a>
                      ),
                    }}
                  />
                }
              />
            </section>
            <section id="sec-misc">
              <h3 className="mt-5 mb-4">
                <FormattedMessage
                  id="Faq.secMisc.title"
                  defaultMessage="Miscellaneous"
                />
              </h3>
              <Qa
                id="what-real-trees"
                q={
                  <FormattedMessage
                    id="Faq.whatRealTrees.q"
                    defaultMessage="Q: Where and what types of real trees do you plant?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.whatRealTrees.a"
                    defaultMessage="<p>We partner with tree-planting organizations working in Africa and East Asia, such as the <a>Trees for the Future</a> and <a2>Eden Reforestation Projects</a2>, to plant fast-growing soil rebuilding trees, fruit trees that diversify nutrition, and trees that can provide forage and fuel-wood. All these trees help make the earth greener and provide long-term financial aid to families who need it most.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                      a: (msg) => (
                        <a
                          className="text-primary-dark"
                          id="real-trees-tft"
                          href="https://trees.org/sponsor/flora/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {msg}
                        </a>
                      ),
                      a2: (msg) => (
                        <a
                          className="text-primary-dark"
                          id="real-trees-tft"
                          href="https://donors.edenprojects.org/user/flora/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {msg}
                        </a>
                      ),
                    }}
                  />
                }
              />
              <Qa
                id="data-in-cloud"
                q={
                  <FormattedMessage
                    id="Faq.dataInCloud.q"
                    defaultMessage="Q: Does Flora keep a copy of my data in the cloud?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.dataInCloud.a"
                    defaultMessage="<p>While most of your data (including the tour progress, garden, to-dos, and stories) are kept in the cloud, Flora currently does not store your <ui>stats</ui> on our servers. This means that <em>you will lose your stats if you re-install the app.</em> We will expand our servers to store everything in the future.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              <Qa
                id="energy-consumption"
                q={
                  <FormattedMessage
                    id="Faq.energyConsumption.q"
                    defaultMessage="Q: How much energy does Flora consume?"
                  />
                }
                a={
                  <FormattedMessage
                    id="Faq.energyConsumption.a"
                    defaultMessage="<p>Although Flora uses a sophisticated algorithm to correctly detect whether you have left the app open during a focus session, we kept the energy consumption in mind when designing the detection mechanism. Based on our experimental results, the app does not consume more energy than an average music-streaming app.</p>"
                    values={{
                      p: (msg) => <p>{msg}</p>,
                      em: (msg) => <em>{msg}</em>,
                      ui: (msg) => `“${msg}”`,
                    }}
                  />
                }
              />
              {appContext.locale === "zh-cn" ? (
                <Qa
                  id="flora-vs-forest"
                  q={
                    <FormattedMessage
                      id="Faq.florVsForest.q"
                      defaultMessage="Q: Are Flora and Forest still partners?"
                    />
                  }
                  a={
                    <FormattedMessage
                      id="Faq.florVsForest.a"
                      defaultMessage="<p>Not anymore. As you may already know, we and the Forest developers are coming from the same lab at a university. The two apps were designed to complement each other, where the Forest app focuses on single-user scenarios and Flora on multi-user tree planting, respectively. However, after Flora launched in August 2017 and gained some popularity, the Forest developers cloned the multi-user planting function in February 2018 and started charging money for this feature. Since then, the partnership between the two apps has terminated. If you enjoy planting trees with friends and like our unique features such as the Grand Tour, to-do list, and habit tracker, please choose Flora.</p>"
                      values={{
                        p: (msg) => <p>{msg}</p>,
                        em: (msg) => <em>{msg}</em>,
                        ui: (msg) => `“${msg}”`,
                      }}
                    />
                  }
                />
              ) : (
                <Qa
                  id="flora-paid-version"
                  q={
                    <FormattedMessage
                      id="Faq.floraPaidVersion.q"
                      defaultMessage="Q: Is there a “paid version” of Flora?"
                    />
                  }
                  a={
                    <FormattedMessage
                      id="Faq.floraPaidVersion.a"
                      defaultMessage="<p>No. There is only one, free version of Flora. Flora is the original app for multi-user tree planting. Any other apps that tried to clone this feature (and even charge it for money) are not associated with us. If you like planting trees with your friends and enjoy our unique features such as the Grand Tour, to-do list, and habit tracker, please choose the original Flora.</p>"
                      values={{
                        p: (msg) => <p>{msg}</p>,
                        em: (msg) => <em>{msg}</em>,
                        ui: (msg) => `“${msg}”`,
                      }}
                    />
                  }
                />
              )}
            </section>
            <p className="mt-5 mb-4">
              <FormattedMessage
                id="Faq.end"
                defaultMessage="If you have any other questions, please contact us via the in-app feedback system or email us at <a>support@flora.appfinca.com</a>."
                values={{
                  a: (msg) => (
                    <a href="mailto:support@flora.appfinca.com">{msg}</a>
                  ),
                  ui: (msg) => `“${msg}”`,
                }}
              />
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
